// The rebinding panel. It sits under the canvas rather than over it so a player
// can open it mid-match without losing sight of the fight.
//
// Recording works off keydown *and* keyup because a chord may be nothing but
// modifiers: "Shift" is a legal binding, and there is no keydown at which the
// player has finished pressing it. So the fullest chord seen while keys are
// going down is held, and committed when the first one comes back up.
import React, { useEffect, useRef, useState } from "react";
import {
  COMMANDS,
  DEFAULT_BINDINGS,
  bindingFromEvent,
  conflictingCommands,
  formatBinding,
  isEmptyBinding,
  sameBinding,
} from "./keyBindings";

const panel = {
  marginTop: "12px",
  padding: "14px 16px",
  width: "800px",
  boxSizing: "border-box",
  color: "#f0e2c0",
  background: "linear-gradient(#3b3120, #2a2214)",
  border: "3px solid #6c5836",
  borderRadius: "8px",
  fontFamily: "Georgia, 'Times New Roman', serif",
};

const heading = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  margin: "0 0 10px",
};

const row = {
  display: "grid",
  gridTemplateColumns: "180px 200px 1fr",
  alignItems: "center",
  gap: "12px",
  padding: "7px 0",
  borderTop: "1px solid rgba(240,226,192,0.16)",
};

const chordButton = (recording) => ({
  cursor: "pointer",
  padding: "8px 10px",
  width: "100%",
  fontSize: "15px",
  fontWeight: 700,
  letterSpacing: "1px",
  fontFamily: "inherit",
  color: recording ? "#2a1c08" : "#f3e6c8",
  background: recording
    ? "linear-gradient(#f5d98a, #d8a441 55%, #b9822b)"
    : "rgba(24,20,12,0.72)",
  border: `2px solid ${recording ? "#7a5518" : "#6c5836"}`,
  borderRadius: "6px",
});

const note = { fontSize: "13px", opacity: 0.7, lineHeight: 1.35 };
const warning = { fontSize: "13px", color: "#f0a04b", lineHeight: 1.35 };

const KeyBindingsEditor = ({
  bindings,
  onRebind,
  onReset,
  onRecordingChange,
}) => {
  // Which command is listening for a chord, or null when none is.
  const [recordingId, setRecordingId] = useState(null);
  // The fullest chord seen since recording started. A ref, not state: it is
  // written from a key handler and read by the next one, and re-rendering
  // between the two would only slow the capture down.
  const pending = useRef(null);

  // The game listens for keys on the window too. While a chord is being
  // recorded it has to stand down, or binding a control group would also recall
  // one — and a digit would go to the match instead of into the box.
  useEffect(() => {
    onRecordingChange(recordingId !== null);
    // Closing the panel mid-recording would otherwise leave the game standing
    // down for good, with nothing left on screen to explain why.
    return () => onRecordingChange(false);
  }, [recordingId, onRecordingChange]);

  useEffect(() => {
    if (recordingId === null) {
      return undefined;
    }
    const finish = (binding) => {
      pending.current = null;
      setRecordingId(null);
      if (binding) {
        onRebind(recordingId, binding);
      }
    };
    const onKeyDown = (event) => {
      // Escape is the way out of a recording, so it can't be bound to anything.
      if (event.code === "Escape") {
        finish(null);
        return;
      }
      // The chord belongs to the game from here, not to the browser: ⌘ + Shift
      // + S would otherwise take a screenshot on the way past.
      event.preventDefault();
      const binding = bindingFromEvent(event);
      // An ordinary key completes the chord, so there is nothing to wait for.
      if (binding.code !== null) {
        finish(binding);
        return;
      }
      pending.current = binding;
    };
    // Reached only for a modifiers-only chord: anything else committed on the
    // way down. The release itself reports the modifier as already up, which is
    // why the chord recorded on the way down is what gets committed.
    const onKeyUp = (event) => {
      event.preventDefault();
      if (pending.current && !isEmptyBinding(pending.current)) {
        finish(pending.current);
      }
    };
    // Clicking away or tabbing out abandons the recording rather than leaving
    // the panel waiting for a key that is never coming.
    const onBlur = () => finish(null);

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [recordingId, onRebind]);

  const conflicts = conflictingCommands(bindings);
  const isDefault = COMMANDS.every(({ id }) =>
    sameBinding(bindings[id], DEFAULT_BINDINGS[id])
  );

  return (
    <div style={panel}>
      <div style={heading}>
        <strong style={{ letterSpacing: "2px" }}>CONTROLS</strong>
        <span style={note}>
          {recordingId === null
            ? "Click a key to change it. Modifiers on their own are allowed."
            : "Press the keys you want — Esc to cancel."}
        </span>
      </div>
      {COMMANDS.map((command) => {
        const recording = recordingId === command.id;
        const clash = conflicts[command.id];
        return (
          <div key={command.id} style={row}>
            <span>
              {command.label}
              <span style={{ ...note, display: "block" }}>
                held, then {command.trigger}
              </span>
            </span>
            <button
              type="button"
              style={chordButton(recording)}
              // The button keeps focus while recording, so a stray click
              // elsewhere blurs it and the recording is abandoned.
              onClick={() => setRecordingId(recording ? null : command.id)}
              aria-label={`${command.label}: ${formatBinding(bindings[command.id])}`}
            >
              {recording ? "Press keys…" : formatBinding(bindings[command.id])}
            </button>
            <span style={clash ? warning : note}>
              {clash
                ? `Same as ${clash.join(", ")} on ${command.trigger} — ${clash.join(", ")} will not answer.`
                : command.hint}
            </span>
          </div>
        );
      })}
      <div style={{ ...row, gridTemplateColumns: "1fr" }}>
        <button
          type="button"
          style={{ ...chordButton(false), width: "200px", cursor: isDefault ? "default" : "pointer", opacity: isDefault ? 0.5 : 1 }}
          onClick={onReset}
          disabled={isDefault}
        >
          Restore defaults
        </button>
      </div>
    </div>
  );
};

export default KeyBindingsEditor;
